import { createSelector } from '@ngrx/store';
import { Map, Seq } from 'immutable';
import { CardRecord } from 'src/store/generated/card-record.generated';
import { OperationRecord } from 'src/store/generated/operation-record.generated';
import { RegionRecord } from 'src/store/generated/region-record.generated';
import { RequestRecord } from 'src/store/generated/request-record.generated';
import { UnitRecord } from 'src/store/generated/unit-record.generated';
import {
  selectAllUnitSummaryEntities,
  UnitSummaryRecord,
} from '../generated/unit-summary-record.generated';
import {
  selectAllCards,
  selectAllOperations,
  selectAllRegions,
  selectAllRequests,
  selectAllUnits,
} from './entity-store-selectors';
import { selectOperationsRegionGroupByCompanyId } from './region-group-selectors';

export function selectRegionsByCompanyId(companyId: string) {
  return createSelector(selectAllRegions, (regions) =>
    regions.filter((r) => r.companyId === companyId),
  );
}

export function selectOperationsRegionsByCompanyId(companyId: string) {
  return createSelector(
    selectOperationsRegionGroupByCompanyId(companyId),
    selectAllRegions,
    (rg, regions) => {
      return rg != null
        ? regions.valueSeq().filter((r) => r.regionGroupId === rg.id)
        : Seq.Indexed<RegionRecord>();
    },
  );
}

export function selectRegionById(regionId: string) {
  return createSelector(selectAllRegions, (regions) => {
    return regions.get(regionId);
  });
}

export function selectRegionByCardNumber(cardNumber: string) {
  return createSelector(
    selectAllCards,
    selectAllRegions,
    selectAllOperations,
    selectAllUnits,
    selectAllUnitSummaryEntities,
    selectAllRequests,
    (cards, regions, operations, units, unitSummaries, requests) => {
      const card = cards.find((c) => c.cardNumber === cardNumber);
      return getRegion(card, regions, operations, units, unitSummaries, requests);
    },
  );
}

export function selectRegionByCardId(cardId: string) {
  return createSelector(
    selectAllCards,
    selectAllRegions,
    selectAllOperations,
    selectAllUnits,
    selectAllUnitSummaryEntities,
    selectAllRequests,
    (cards, regions, operations, units, unitSummaries, requests) => {
      const card = cards.get(cardId);
      return getRegion(card, regions, operations, units, unitSummaries, requests);
    },
  );
}

function getRegion(
  card: CardRecord | undefined,
  regions: Map<string, RegionRecord>,
  operations: Map<string, OperationRecord>,
  units: Map<string, UnitRecord>,
  unitSummaries: Map<string, UnitSummaryRecord>,
  requests: Map<string, RequestRecord>,
) {
  if (card != null) {
    // Look up region based on unit
    if (card.operationId != null) {
      const operation = operations.get(card.operationId);
      if (operation != null && operation.unitId != null) {
        const unit = units.get(operation.unitId) ?? unitSummaries.get(operation.unitId);
        if (unit != null) {
          const region = regions.get(unit.regionId);
          if (region != null) {
            return region;
          }
        }
      }
    }

    // otherwise look up based on request
    if (card.requestId != null) {
      const request = requests.get(card.requestId);
      if (request != null && request.regionId != null) {
        const region = regions.get(request.regionId);
        if (region != null) {
          return region;
        }
      }
    }
  }
  return undefined;
}

export function selectRegionsByCompanyWithCommunicationCenterIdOrUnAssigned(
  companyId: string,
  commicationCenterId: string,
) {
  return createSelector(selectAllRegions, (regions) =>
    regions.filter(
      (r) =>
        r.companyId === companyId &&
        (r.communicationCenterId === commicationCenterId || r.communicationCenterId == null),
    ),
  );
}

export function selectRegionsByCompanyAndCommunicationCenterId(
  companyId: string,
  commicationCenterId: string,
) {
  return createSelector(selectAllRegions, (regions) =>
    regions.filter(
      (r) => r.companyId === companyId && r.communicationCenterId === commicationCenterId,
    ),
  );
}

export function selectRegionsByCommunicationCenterId(commicationCenterId: string) {
  return createSelector(selectAllRegions, (regions) =>
    regions.filter((r) => r.communicationCenterId === commicationCenterId),
  );
}

export function selectRegionsWithoutCommunicationCenter(companyId: string) {
  return createSelector(selectAllRegions, (regions) =>
    regions.filter((r) => r.companyId === companyId && r.communicationCenterId == null),
  );
}
